import { FC, useEffect } from "react";

import { StoreProviderOutlet } from "@/app/outlets/StoreProviderOutlet";
import { appRoutes } from "@/app/router/appRoutes";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import { usePublicAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { IntegrationsSlackCallbackDesktopHandoffPage } from "./integrations-slack-callback-desktop-handoff-page";
import { IntegrationsSlackCallbackIosHandoffPage } from "./integrations-slack-callback-ios-handoff-page";
import {
  getSlackOAuthClientKindFromState,
  SlackOAuthClientKind,
} from "@/domains/integrations/slack-oauth-client-kind";

export const IntegrationsSlackCallbackRoute: FC = observer(() => {
  const { publicStore } = usePublicAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const returnedStateClientKind = getSlackOAuthClientKindFromState(
    new URLSearchParams(location.search).get("state")
  );
  const isDesktop = clientEnvModule.isDesktop();
  const shouldHandoffToDesktop =
    !isDesktop && returnedStateClientKind === SlackOAuthClientKind.Desktop;
  const shouldHandoffToIos = !isDesktop && returnedStateClientKind === SlackOAuthClientKind.Ios;
  const shouldRedirectToLogin =
    !publicStore.auth.isStandardMode &&
    !isDesktop &&
    !shouldHandoffToDesktop &&
    !shouldHandoffToIos;

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      return;
    }

    const redirectToLogin = async () => {
      await localStorageModule.writePendingRedirectPath({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });

      navigate(appRoutes.logIn({}).path, { replace: true });
    };

    void redirectToLogin();
  }, [location.hash, location.pathname, location.search, navigate, shouldRedirectToLogin]);

  if (publicStore.auth.isStandardMode) {
    return <StoreProviderOutlet />;
  }

  if (isDesktop) {
    return null;
  }

  if (shouldHandoffToDesktop) {
    return <IntegrationsSlackCallbackDesktopHandoffPage />;
  }

  if (shouldHandoffToIos) {
    return <IntegrationsSlackCallbackIosHandoffPage />;
  }

  return null;
});
